// src/components/ResultsPanel.jsx — ohapotato v2026.10.36
import React, { useState } from 'react';
import RecipeCard from './RecipeCard';
import PlaceholderState from './PlaceholderState';
import LoadingState from './LoadingState';

const PAGE_SIZE = 8;

export default function ResultsPanel({
  recipes, loading, error, hasSearched,
  user, savedIds, onSave, resultsRef, onSuggest,
  onAddToGrocery, onRemoveFromGrocery,
}) {
  const [page, setPage] = useState(1);

  const sorted   = [...recipes].sort((a, b) => (b.matchPercent || 0) - (a.matchPercent || 0));
  const visible  = sorted.slice(0, page * PAGE_SIZE);
  const hasMore  = visible.length < sorted.length;
  const remaining = sorted.length - visible.length;

  // Reset page when recipes change (new search)
  React.useEffect(() => { setPage(1); }, [recipes]);

  return (
    <div ref={resultsRef}>
      {loading ? (
        <LoadingState />
      ) : error ? (
        <div style={styles.state}>
          <div style={styles.stateIcon}>⚠️</div>
          <h3 style={styles.stateTitle}>Something went wrong</h3>
          <p style={styles.stateText}>{error}</p>
        </div>
      ) : !hasSearched ? (
        <PlaceholderState onSuggest={onSuggest} />
      ) : recipes.length === 0 ? (
        <div style={styles.state}>
          <div style={styles.stateIcon}>🔍</div>
          <h3 style={styles.stateTitle}>No recipes found</h3>
          <p style={styles.stateText}>Try adding more ingredients or different combinations.</p>
        </div>
      ) : (
        <div style={styles.panel}>
          <div style={styles.header}>
            <h2 style={styles.title}>Your Recipes</h2>
            <span style={styles.count}>{recipes.length} found</span>
          </div>

          <div style={styles.list}>
            {visible.map((recipe, i) => (
              <RecipeCard
                key={recipe.id || i}
                recipe={recipe}
                index={i}
                user={user}
                savedIds={savedIds}
                onSave={onSave}
                onAddToGrocery={onAddToGrocery}
                onRemoveFromGrocery={onRemoveFromGrocery}
              />
            ))}
          </div>

          {hasMore && (
            <button
              style={styles.showMoreBtn}
              onClick={() => setPage(p => p + 1)}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--accent-orange)';
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.borderColor = 'var(--accent-orange)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--text-secondary)';
                e.currentTarget.style.borderColor = 'var(--border-base)';
              }}
            >
              Show {Math.min(remaining, PAGE_SIZE)} more
              {remaining > PAGE_SIZE ? ` · ${remaining} remaining` : ''}
               ↓
            </button>
          )}

          {!hasMore && sorted.length > PAGE_SIZE && (
            <p style={styles.allShown}>All {sorted.length} recipes shown</p>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  panel: { display: 'flex', flexDirection: 'column' },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '14px',
  },
  title: {
    fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: '400',
    letterSpacing: '-0.3px', color: 'var(--text-primary)',
    transition: 'color 0.25s ease',
  },
  count: {
    fontSize: '12px', fontWeight: '500', color: 'var(--text-muted)',
    background: 'var(--bg-pill)', padding: '4px 12px',
    borderRadius: 'var(--radius-pill)',
  },
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  showMoreBtn: {
    marginTop: '16px',
    width: '100%', padding: '12px',
    border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-md)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.14s ease',
    outline: 'none',
  },
  allShown: {
    marginTop: '16px', textAlign: 'center',
    fontSize: '11px', color: 'var(--text-muted)',
    fontFamily: 'var(--font-body)', fontStyle: 'italic',
  },
  state: {
    background: 'var(--bg-card)', border: '1.5px dashed var(--border-base)',
    borderRadius: 'var(--radius-lg)', padding: '56px 32px',
    textAlign: 'center', animation: 'fadeUp 0.3s ease',
  },
  stateIcon:  { fontSize: '40px', marginBottom: '14px' },
  stateTitle: {
    fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: '400',
    color: 'var(--text-primary)', marginBottom: '8px',
    letterSpacing: '-0.3px', transition: 'color 0.25s ease',
  },
  stateText: {
    fontSize: '14px', color: 'var(--text-muted)',
    lineHeight: '1.6', transition: 'color 0.25s ease',
  },
};
